import { callApi } from '@/api/callApi';
import { API_ENDPOINT, API_URL_ORDER_SERVICE } from '@/api/constant';
import CommonTable, {
  Action,
  TableColumn,
} from '@/components/common/CommonTable';
import ConfirmationModel from '@/components/common/models/ConfrimationModel';
import PageHeading from '@/components/common/PageHeading';
import Pagination from '@/components/common/Pagination';
import Layout from '@/components/Layout/Layout';
import Card from '@/components/UI/Card';
import FormField from '@/components/UI/FormField';
import { useSelectableTable } from '@/hooks/useSelectableTable';
import { PageInfo } from '@/interfaces/common.interface';
import {
  ICouponResponseData,
  ICouponResponseList,
} from '@/interfaces/cuponResponse.interface';
import { GetFilterUrl } from '@/utility/helper';
import { RoutesLink } from '@/utility/RouteLinks';
import { PlusSquare } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface CouponStates {
  isLoading: boolean;
  couponStatus: string;
  coupons: ICouponResponseData[];
  pageInfo: PageInfo;
  selectedCoupons: Set<string | number>;
  showDeleteModel: boolean;
  couponToDelete: string | null;
  sortingOption: string;
}
const CouponsList = () => {
  const navigate = useNavigate();
  const [states, setStates] = useState<CouponStates>({
    isLoading: false,
    couponStatus: '',
    coupons: [],
    pageInfo: {
      total: 0,
      totalPages: 0,
      limit: 15,
      page: 1,
    },
    selectedCoupons: new Set(),
    showDeleteModel: false,
    couponToDelete: null,
    sortingOption: '',
  });

  const handleChangeState = (newState: Partial<typeof states>) => {
    setStates((prev) => ({
      ...prev,
      ...newState,
    }));
  };

  const fetchCouponsList = async () => {
    try {
      const filterQuery = {
        page: states.pageInfo.page,
        limit: states.pageInfo.limit,
        sort: states.sortingOption,
      };
      const query = GetFilterUrl(filterQuery);
      handleChangeState({ isLoading: true });
      const PATH = `${API_URL_ORDER_SERVICE}${API_ENDPOINT.coupons}?${query ? query : ''}`;
      const res = await callApi<ICouponResponseList>(PATH, { method: 'GET' });
      handleChangeState({
        coupons: res.data,
        pageInfo: {
          limit: +res.pageInfo.limit,
          page: +res.pageInfo.page,
          total: +res.pageInfo.total,
          totalPages: +res.pageInfo.totalPages,
        },
        isLoading: false,
      });
    } catch (error) {
      console.log('error is', error);
    }
  };

  const handleDeleteCoupon = async (id: string) => {
    try {
      const PATH = `${API_URL_ORDER_SERVICE}${API_ENDPOINT.coupons}/${id}`;
      await callApi(PATH, { method: 'DELETE' });
    } catch (error) {
      console.log('error is delete coupon', error);
    } finally {
      fetchCouponsList();
    }
  };

  useEffect(() => {
    fetchCouponsList();
  }, [states.pageInfo.page, states.pageInfo.limit, states.sortingOption]);

  const columns: TableColumn[] = [
    {
      key: 'code',
      header: 'Code',
    },
    {
      key: 'name',
      header: 'Name',
    },
    {
      key: 'type',
      header: 'Coupon Type',
    },
    {
      key: 'discountValue',
      header: 'Discount Value',
    },
    {
      key: 'maxDiscount',
      header: 'Max Discount',
    },
    {
      key: 'minimumValue',
      header: 'Minimum Value',
    },
  ];

  const actions: Action<ICouponResponseData>[] = [
    {
      text: 'Edit',
      onClick: (coupon) => navigate(`${RoutesLink.addCoupon}/${coupon.code}`),
    },
    {
      text: 'Delete',
      onClick: (coupon) => {
        handleChangeState({ showDeleteModel: true, couponToDelete: coupon.id });
      },
    },
  ];

  const sortingOption = [
    { label: 'Newest', value: 'newest' },
    { label: 'Oldest', value: 'oldest' },
    { label: 'A To Z', value: 'az' },
    { label: 'Z To A', value: 'za' },
  ];

  const { selectedItems, handleSelectAll, handleSelectItem } =
    useSelectableTable(states.coupons);

  const handleChangePage = (page: number) => {
    handleChangeState({
      pageInfo: {
        ...states.pageInfo,
        page,
      },
    });
  };

  return (
    <Layout>
      <PageHeading
        text="Coupons"
        button={{
          icon: <PlusSquare />,
          text: 'Create Coupon',
          onClick: () => {
            navigate(RoutesLink.addCoupon);
          },
        }}
      />
      <Card>
        <div className="flex justify-end">
          <div>
            <FormField
              type="select"
              options={sortingOption}
              value={states.sortingOption}
              onChange={(e) =>
                handleChangeState({ sortingOption: e.target.value })
              }
            />
          </div>
        </div>
        <CommonTable
          columns={columns}
          data={states.coupons}
          showCheckbox={false}
          selectedItems={selectedItems}
          onSelectAll={handleSelectAll}
          onSelectItem={handleSelectItem}
          getItemId={(item) => item.id}
          emptyMessage="No coupons found"
          loading={states.isLoading}
          actions={actions}
        />
        <Pagination
          paginationData={states.pageInfo}
          isLoading={states.isLoading}
          onPageChange={(page) => handleChangePage(page)}
        />
      </Card>
      {states.showDeleteModel && (
        <ConfirmationModel
          type="coupon"
          onClose={() =>
            handleChangeState({ showDeleteModel: false, couponToDelete: null })
          }
          onConfirm={() => handleDeleteCoupon(states.couponToDelete as string)}
          show={states.showDeleteModel}
        />
      )}
    </Layout>
  );
};

export default CouponsList;
